/**
 * Действия режима «Предложения».
 *
 * Каждое предложение — отдельный прогон набора. Сессия хранится в состоянии
 * экрана, поэтому перерисовка её не теряет.
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import type { SessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { advanceSentence, finishSentences, sentencesScreen } from '../state/sentences.js';

/** Действия режима, которые приложение передаёт экранам. */
export interface SentencesActions {
  readonly onStartSentences: () => void;
  readonly onSentenceDone: (run: TypingSession) => void;
}

/** Что нужно режиму от приложения. */
export interface SentencesDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
  readonly submit: (payload: SessionPayload) => Promise<unknown>;
}

export function useSentences(runtime: Runtime, deps: SentencesDeps): SentencesActions {
  const { workspace, commit, screen, setScreen, submit } = deps;

  const onStartSentences = useCallback(() => {
    if (workspace === null) return;

    const next = sentencesScreen(runtime, workspace);
    if (next !== null) setScreen(next);
  }, [runtime, workspace, setScreen]);

  const onSentenceDone = useCallback(
    (run: TypingSession) => {
      if (workspace === null || screen.name !== 'sentences') return;

      const session = advanceSentence(screen.session, run);
      if (!session.finished) {
        setScreen({ ...screen, session });
        return;
      }

      const transition = finishSentences(runtime, workspace, session, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, setScreen, submit],
  );

  return { onStartSentences, onSentenceDone };
}
